<script setup lang="ts">
import { computed, inject, onMounted, ref } from "vue";
import {
  EntityBasicDetailsDto,
  EntityFormElement,
  FormElementType,
} from "~/api";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { apiClient } from "~/utils/apiClient";
import {
  FormResponseState,
  formResponseInjectKey,
} from "../../composables/useFormResponse";
import { DefaultFormComponentProps } from "./FormComponentRenderer.vue";

const props = defineProps<DefaultFormComponentProps<EntityFormElement>>();

const { useAnswer } = inject(
  formResponseInjectKey,
  // Cast the type since we know it is always provided. For more info see:
  // https://vuejs.org/guide/typescript/composition-api.html#typing-provide-inject
) as FormResponseState;
const answer = useAnswer<FormElementType.ENTITY>(props.modelValue);
const elementProperties = computed(() => props.modelValue.properties);
const myInfoStore = useMyInfoStore();

const entities = ref<EntityBasicDetailsDto[]>([]);

onMounted(async () => {
  if (!elementProperties.value.entityDefinitionGuid) {
    return;
  }

  await apiClient.entity
    .getBasicEntityDetails(
      elementProperties.value.entityDefinitionGuid ?? "",
      myInfoStore.currentSysOrgGuid,
    )
    .then((res) => {
      entities.value = res;
    });
});
</script>

<template>
  <div class="EntityComponent">
    <slot name="label" :element="modelValue" />

    <div style="flex-grow: 1"></div>

    <el-select
      :model-value="answer ?? undefined"
      :disabled="isReadOnly"
      @update:model-value="answer = $event ?? null"
    >
      <el-option
        v-for="entity in entities"
        :key="entity.guid"
        :label="entity.name"
        :value="entity.guid"
      >
      </el-option>
    </el-select>
  </div>
</template>

<style lang="less">
.el-select-dropdown {
  .el-select-dropdown__item {
    font-size: 1.3em;
  }

  .component-question-code {
    border: 1px solid var(--el-color-danger);
    width: fit-content;
    height: fit-content;
    padding: 0px 6px;
    margin: 0px 0 0 12px;
    background-color: var(--el-color-info-light-9);
    color: var(--el-text-color-primary);
    border-radius: 0.5em;
  }
}
.EntityComponent {
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;

  .el-select__selection {
    text-align: right;
  }
  .el-select {
    .el-select__wrapper.is-disabled,
    .el-select__wrapper.is-disabled.is-hovering {
      box-shadow: 0 0 0 1px var(--el-disabled-border-color) inset;
      background-color: #f2f2f2;
      .el-select__placeholder {
        color: #444142;
      }
    }
  }
}

body.forPdf .EntityComponent {
  .el-select__wrapper {
    box-shadow: 0 0 0 1px var(--el-disabled-border-color) inset;
    background-color: transparent !important;
    .el-select__placeholder {
      color: black !important;
    }
  }
}
</style>
